/**
 * shared/harry-brief.ts - Harry's watch brief: what Harry (market research) watches for ONE workspace, and where.
 *
 * Draper writes it (set_harry_brief), the owner edits it on the Harry page, or it is auto-drafted from the business profile.
 * Stored in harry_watch_briefs (server/agents/harry-watch.ts). Shared by the server and the client, so it has no server imports.
 *
 * normalizeHarryBrief() is the ONLY way a brief is built: it accepts loose input (arrays or comma / newline separated strings,
 * partial channel maps), trims, de-duplicates (case-insensitive), clamps every list to its limit, drops invalid sites, and reports
 * everything it dropped. The result always passes harryBriefSchema.
 */
import { z } from "zod";

export const HARRY_BRIEF_LIMITS = {
  topics: 15,
  competitors: 10,
  audienceQuestions: 10,
  extraSites: 10,
  topicChars: 120,
  questionChars: 200,
  locationChars: 120,
  urlChars: 500,
} as const;

export const HARRY_CHANNELS = ["googleNews", "googleSearch", "googleTrends", "reddit", "x"] as const;
export type HarryChannel = (typeof HARRY_CHANNELS)[number];

export const HARRY_CHANNEL_LABELS: Record<HarryChannel, string> = {
  googleNews: "Google News",
  googleSearch: "Google search",
  googleTrends: "Google Trends",
  reddit: "Reddit",
  x: "X (partial)",
};

export const harryBriefChannelsSchema = z.object({
  googleNews: z.boolean(),
  googleSearch: z.boolean(),
  googleTrends: z.boolean(),
  reddit: z.boolean(),
  x: z.boolean(),
});
export type HarryBriefChannels = z.infer<typeof harryBriefChannelsSchema>;

export const harryBriefSchema = z.object({
  topics: z.array(z.string().min(1).max(HARRY_BRIEF_LIMITS.topicChars)).max(HARRY_BRIEF_LIMITS.topics),
  competitors: z.array(z.string().min(1).max(HARRY_BRIEF_LIMITS.topicChars)).max(HARRY_BRIEF_LIMITS.competitors),
  location: z.string().min(1).max(HARRY_BRIEF_LIMITS.locationChars).nullable(),
  audienceQuestions: z.array(z.string().min(1).max(HARRY_BRIEF_LIMITS.questionChars)).max(HARRY_BRIEF_LIMITS.audienceQuestions),
  channels: harryBriefChannelsSchema,
  extraSites: z.array(z.string().url().max(HARRY_BRIEF_LIMITS.urlChars)).max(HARRY_BRIEF_LIMITS.extraSites),
  language: z.literal("en"),
  updatedBy: z.enum(["draper", "owner", "auto"]),
  updatedAt: z.string(),
});
export type HarryBrief = z.infer<typeof harryBriefSchema>;
export type HarryBriefAuthor = HarryBrief["updatedBy"];

/** Loose input accepted from Draper, the owner form and the auto-draft. Every field is optional (a partial update). */
const looseList = z.union([z.array(z.unknown()), z.string(), z.null()]).optional();
export const harryBriefInputSchema = z.object({
  topics: looseList,
  competitors: looseList,
  location: z.union([z.string(), z.null()]).optional(),
  audienceQuestions: looseList,
  channels: z.record(z.unknown()).optional(),
  extraSites: looseList,
  language: z.unknown().optional(),
}).passthrough();
export type HarryBriefInput = z.input<typeof harryBriefInputSchema>;

export const DEFAULT_HARRY_CHANNELS: HarryBriefChannels = { googleNews: true, googleSearch: true, googleTrends: true, reddit: true, x: true };

export function emptyHarryBrief(updatedBy: HarryBriefAuthor = "auto", now: Date = new Date()): HarryBrief {
  return { topics: [], competitors: [], location: null, audienceQuestions: [], channels: { ...DEFAULT_HARRY_CHANNELS }, extraSites: [], language: "en", updatedBy, updatedAt: now.toISOString() };
}

export interface HarryBriefDrop { field: string; value: string; reason: string }

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g;

function cleanText(v: unknown): string {
  if (typeof v !== "string" && typeof v !== "number") return "";
  return String(v).replace(CONTROL_CHARS, " ").replace(/\s+/g, " ").trim();
}

/** Cut at a word boundary when possible. */
function clampText(s: string, max: number): string {
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const sp = cut.lastIndexOf(" ");
  return (sp > max * 0.6 ? cut.slice(0, sp) : cut).trim();
}

function toList(v: unknown, splitCommas: boolean): unknown[] {
  if (v === undefined || v === null) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === "string") return v.split(splitCommas ? /[\n,;]+/ : /\n+/);
  return [];
}

function cleanList(field: string, v: unknown, limit: number, maxChars: number, drops: HarryBriefDrop[], splitCommas = true): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of toList(v, splitCommas)) {
    const t = cleanText(raw).replace(/^["'\s]+|["'\s]+$/g, "");
    if (!t) continue;
    const s = clampText(t, maxChars);
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    if (out.length >= limit) { drops.push({ field, value: s, reason: `over the limit of ${limit}` }); continue; }
    out.push(s);
  }
  return out;
}

const PRIVATE_HOST = /^(localhost|.*\.localhost|.*\.local|.*\.internal|0\.0\.0\.0|127\.\d+\.\d+\.\d+|10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|169\.254\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+|\[?::1\]?|metadata\.google\.internal)$/i;

/** A public http(s) URL or null. Adds https:// to a bare domain. No credentials, no private / internal hosts (the fetch re-checks DNS). */
export function normalizeSiteUrl(raw: unknown): { url: string | null; reason?: string } {
  let s = cleanText(raw);
  if (!s) return { url: null, reason: "empty" };
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) s = `https://${s}`;
  let u: URL;
  try { u = new URL(s); } catch { return { url: null, reason: "not a valid URL" }; }
  if (u.protocol !== "http:" && u.protocol !== "https:") return { url: null, reason: "only http(s) links" };
  if (u.username || u.password) return { url: null, reason: "links with a username or password are not allowed" };
  if (!u.hostname.includes(".") || PRIVATE_HOST.test(u.hostname)) return { url: null, reason: "not a public website" };
  u.hash = "";
  const out = u.toString();
  if (out.length > HARRY_BRIEF_LIMITS.urlChars) return { url: null, reason: "URL is too long" };
  return { url: out };
}

function cleanSites(v: unknown, drops: HarryBriefDrop[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of toList(v, true)) {
    const shown = cleanText(raw);
    if (!shown) continue;
    const { url, reason } = normalizeSiteUrl(shown);
    if (!url) { drops.push({ field: "extraSites", value: shown.slice(0, 120), reason: reason || "invalid" }); continue; }
    if (seen.has(url.toLowerCase())) continue;
    seen.add(url.toLowerCase());
    if (out.length >= HARRY_BRIEF_LIMITS.extraSites) { drops.push({ field: "extraSites", value: url, reason: `over the limit of ${HARRY_BRIEF_LIMITS.extraSites}` }); continue; }
    out.push(url);
  }
  return out;
}

function cleanLocation(v: unknown): string | null {
  const s = cleanText(v);
  if (!s || /^(none|null|n\/a|unknown|-)$/i.test(s)) return null;
  return clampText(s, HARRY_BRIEF_LIMITS.locationChars);
}

function toBool(v: unknown): boolean | undefined {
  if (typeof v === "boolean") return v;
  if (v === "true" || v === 1 || v === "1" || v === "on") return true;
  if (v === "false" || v === 0 || v === "0" || v === "off") return false;
  return undefined;
}

/**
 * Build a valid brief from loose input. Fields absent from `input` keep their value from `base` (a partial update);
 * a field present in `input` replaces it (lists replace, channels merge key by key).
 */
export function normalizeHarryBrief(
  input: unknown,
  opts: { base?: HarryBrief | null; updatedBy: HarryBriefAuthor; now?: Date },
): { brief: HarryBrief; dropped: HarryBriefDrop[] } {
  const base = opts.base ?? emptyHarryBrief(opts.updatedBy, opts.now);
  const src = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const has = (k: string) => Object.prototype.hasOwnProperty.call(src, k) && src[k] !== undefined;
  const drops: HarryBriefDrop[] = [];
  const L = HARRY_BRIEF_LIMITS;

  const channels: HarryBriefChannels = { ...DEFAULT_HARRY_CHANNELS, ...base.channels };
  if (src.channels && typeof src.channels === "object") {
    for (const k of HARRY_CHANNELS) {
      const b = toBool((src.channels as Record<string, unknown>)[k]);
      if (b !== undefined) channels[k] = b;
    }
  }

  const brief: HarryBrief = {
    topics: has("topics") ? cleanList("topics", src.topics, L.topics, L.topicChars, drops) : cleanList("topics", base.topics, L.topics, L.topicChars, drops),
    competitors: has("competitors") ? cleanList("competitors", src.competitors, L.competitors, L.topicChars, drops) : cleanList("competitors", base.competitors, L.competitors, L.topicChars, drops),
    location: has("location") ? cleanLocation(src.location) : cleanLocation(base.location),
    // questions often contain commas, so only newlines (or array items) separate them
    audienceQuestions: has("audienceQuestions")
      ? cleanList("audienceQuestions", src.audienceQuestions, L.audienceQuestions, L.questionChars, drops, false)
      : cleanList("audienceQuestions", base.audienceQuestions, L.audienceQuestions, L.questionChars, drops, false),
    channels,
    extraSites: has("extraSites") ? cleanSites(src.extraSites, drops) : cleanSites(base.extraSites, drops),
    language: "en",
    updatedBy: opts.updatedBy,
    updatedAt: (opts.now ?? new Date()).toISOString(),
  };
  return { brief: harryBriefSchema.parse(brief), dropped: drops };
}

/** Field names whose value differs between two briefs (ignores updatedBy / updatedAt). */
export function changedBriefFields(a: HarryBrief | null | undefined, b: HarryBrief): string[] {
  const keys: Array<keyof HarryBrief> = ["topics", "competitors", "location", "audienceQuestions", "channels", "extraSites"];
  if (!a) return keys.filter((k) => JSON.stringify(b[k]) !== JSON.stringify(emptyHarryBrief()[k]));
  return keys.filter((k) => JSON.stringify(a[k]) !== JSON.stringify(b[k]));
}
